/**
 * PresentationRenderer — universal presentation dispatcher.
 *
 * This is the ONLY component that selects which visual component renders
 * a workspace_presentation. WorkspacePage, MessageList, and ChatBubble
 * should all route through here — never check wp.layout directly.
 *
 * Props:
 *   wp           — workspace_presentation from the backend
 *   content      — raw text content (used as fallback)
 *   onAction     — optional callback(actionId) for copy/save/share
 *   fallback     — optional React node to render when no renderer found
 *   AthenaPresentationEngine — existing renderer for non-structured text
 *   cognitiveProfile — passed through to engines that need it
 *   streaming    — true while the message is still streaming
 */

import React from 'react';
import { getRenderer, hasPremiumRenderer } from './registry';

export default function PresentationRenderer({
  wp,
  content,
  onAction,
  fallback,
  AthenaPresentationEngine,
  cognitiveProfile,
  streaming = false,
}) {
  // Streaming: never render a partial structured presentation
  if (streaming && hasPremiumRenderer(wp)) {
    return (
      <div style={{ color: 'rgba(150,180,220,0.6)', fontSize: 13 }}>
        ✦ Preparando apresentação...
      </div>
    );
  }

  if (wp && wp.layout) {
    const Component = getRenderer(wp.layout);

    if (Component) {
      return (
        <Component
          data={wp}       // full wp object — backward-compatible with RecipeShowcaseRenderer
          wp={wp}         // also as wp for new-style renderers
          onAction={onAction}
        />
      );
    }

    // Unknown layout — log and fall through
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[PresentationRenderer] unknown layout:', wp.layout, wp.schema_version);
    }
  }

  // Fallback: text renderer
  if (fallback) return fallback;

  if (AthenaPresentationEngine) {
    return <AthenaPresentationEngine content={content} cognitiveProfile={cognitiveProfile} />;
  }

  return (
    <div style={{ whiteSpace: 'pre-wrap' }}>
      {content}
    </div>
  );
}
